/**
 * 音声API サービス
 * 音声録音・アップロード・解析関連のAPI呼び出し
 */
import axios from '../../../utils/axiosConfig';

const BACKEND_URL = process.env.REACT_APP_API_URL;

/**
 * 音声録音サービス
 */
export const audioService = {
  /**
   * 録音同意を記録
   * @param {number} checkSheetId - チェックシートID
   * @param {Object} consentData - 同意データ
   * @returns {Promise<Object>} 同意記録
   */
  recordConsent: async (checkSheetId, consentData) => {
    const response = await axios.post(
      `${BACKEND_URL}/audio/consent/${checkSheetId}`,
      consentData,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 音声ファイルをアップロード
   * @param {number} checkSheetId - チェックシートID
   * @param {FormData} formData - ファイルデータ
   * @param {string} sourceType - ソースタイプ (browser/upload)
   * @param {number} consentId - 同意ID
   * @param {Function} onProgress - 進捗コールバック
   * @returns {Promise<Object>} アップロード結果
   */
  uploadAudio: async (checkSheetId, formData, sourceType, consentId, onProgress) => {
    const response = await axios.post(
      `${BACKEND_URL}/audio/upload/${checkSheetId}`,
      formData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          source_type: sourceType,
          consent_id: consentId,
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    return response.data;
  },

  /**
   * チャンクアップロード（5分ごとの分割アップロード用）
   * @param {string} sessionId - セッションID
   * @param {number} chunkIndex - チャンク番号
   * @param {Blob} chunkBlob - チャンクデータ
   * @param {Function} onProgress - 進捗コールバック
   * @returns {Promise<Object>} アップロード結果
   */
  uploadChunk: async (sessionId, chunkIndex, chunkBlob, onProgress) => {
    const formData = new FormData();
    formData.append('file', chunkBlob, `chunk_${chunkIndex}.webm`);

    const response = await axios.post(
      `${BACKEND_URL}/audio/upload/chunk/${sessionId}`,
      formData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          chunk_index: chunkIndex,
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    return response.data;
  },

  /**
   * チャンクアップロードを完了し結合
   * @param {string} sessionId - セッションID
   * @param {number} checkSheetId - チェックシートID
   * @param {number} consentId - 同意ID
   * @returns {Promise<Object>} 最終的な録音データ
   */
  finalizeChunks: async (sessionId, checkSheetId, consentId) => {
    const response = await axios.post(
      `${BACKEND_URL}/audio/upload/finalize/${sessionId}`,
      null,
      {
        withCredentials: true,
        params: {
          check_sheet_id: checkSheetId,
          consent_id: consentId,
        },
      }
    );
    return response.data;
  },

  /**
   * 録音一覧を取得
   * @param {number} checkSheetId - チェックシートID
   * @returns {Promise<Array>} 録音一覧
   */
  getRecordings: async (checkSheetId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/recordings/${checkSheetId}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 録音を削除
   * @param {number} recordingId - 録音ID
   * @returns {Promise<void>}
   */
  deleteRecording: async (recordingId) => {
    await axios.delete(
      `${BACKEND_URL}/audio/recordings/${recordingId}`,
      { withCredentials: true }
    );
  },

  /**
   * 解析を開始
   * @param {number} recordingId - 録音ID
   * @param {Object} options - 解析オプション
   * @returns {Promise<Object>} ジョブ情報
   */
  startAnalysis: async (recordingId, options = {}) => {
    const response = await axios.post(
      `${BACKEND_URL}/audio/analysis/start/${recordingId}`,
      options,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 解析ジョブのステータスを取得
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} ジョブステータス
   */
  getAnalysisStatus: async (jobId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/analysis/jobs/${jobId}`,
      { withCredentials: true }
    );
    return response.data;
  },
};

/**
 * 音声解析サービス
 */
export const analysisService = {
  /**
   * 解析ジョブを開始
   * @param {number} recordingId - 録音ID
   * @param {Object} options - 解析オプション
   * @returns {Promise<Object>} ジョブ情報
   */
  startAnalysis: async (recordingId, options = {}) => {
    const response = await axios.post(
      `${BACKEND_URL}/audio/analysis/start/${recordingId}`,
      options,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * ジョブステータスを取得
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} ジョブステータス
   */
  getJobStatus: async (jobId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/analysis/jobs/${jobId}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * ジョブをキャンセル
   * @param {number} jobId - ジョブID
   * @returns {Promise<void>}
   */
  cancelJob: async (jobId) => {
    await axios.post(
      `${BACKEND_URL}/audio/analysis/jobs/${jobId}/cancel`,
      null,
      { withCredentials: true }
    );
  },

  /**
   * ジョブを再試行
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} 新しいジョブ情報
   */
  retryJob: async (jobId) => {
    const response = await axios.post(
      `${BACKEND_URL}/audio/analysis/jobs/${jobId}/retry`,
      null,
      { withCredentials: true }
    );
    return response.data;
  },
};

/**
 * 解析結果サービス
 */
export const resultsService = {
  /**
   * 書き起こし結果を取得
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} 書き起こし結果
   */
  getTranscription: async (jobId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/results/transcription/${jobId}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 音響解析結果を取得
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} 音響解析結果
   */
  getAcousticAnalysis: async (jobId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/results/acoustic/${jobId}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 言語解析結果を取得
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} 言語解析結果
   */
  getLanguageAnalysis: async (jobId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/results/language/${jobId}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * スコア結果を取得
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} スコア結果
   */
  getScores: async (jobId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/results/scores/${jobId}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 完全な解析結果を取得
   * @param {number} checkSheetId - チェックシートID
   * @returns {Promise<Object>} 完全な解析結果
   */
  getFullResults: async (checkSheetId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/results/full/${checkSheetId}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * ジョブIDで完全な解析結果を取得
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} 完全な解析結果
   */
  getFullResultsByJobId: async (jobId) => {
    // 各結果を並列取得
    const [transcription, acoustic, language, scores] = await Promise.allSettled([
      axios.get(`${BACKEND_URL}/audio/results/transcription/${jobId}`, { withCredentials: true }),
      axios.get(`${BACKEND_URL}/audio/results/acoustic/${jobId}`, { withCredentials: true }),
      axios.get(`${BACKEND_URL}/audio/results/language/${jobId}`, { withCredentials: true }),
      axios.get(`${BACKEND_URL}/audio/results/scores/${jobId}`, { withCredentials: true }),
    ]);

    return {
      transcription: transcription.status === 'fulfilled' ? transcription.value.data : null,
      acoustic_analysis: acoustic.status === 'fulfilled' ? acoustic.value.data : null,
      language_analysis: language.status === 'fulfilled' ? language.value.data : null,
      scores: scores.status === 'fulfilled' ? scores.value.data : null,
    };
  },
};

/**
 * 音声解析専用セッションサービス
 */
export const audioOnlyService = {
  /**
   * 音声解析専用セッションを作成
   * @param {string} candidateName - 応募者名
   * @param {string} interviewerName - 面接官名
   * @param {string|null} position - 職種（任意）
   * @returns {Promise<Object>} セッション情報
   */
  createSession: async (candidateName, interviewerName, position = null) => {
    const response = await axios.post(
      `${BACKEND_URL}/audio/session/create`,
      {
        candidate_name: candidateName,
        interviewer_name: interviewerName,
        position: position,
      },
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 音声解析専用セッション一覧を取得
   * @returns {Promise<Array>} セッション一覧
   */
  getSessions: async () => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/session/list`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 音声解析専用セッションの詳細を取得
   * @param {number} sessionId - セッションID
   * @returns {Promise<Object>} セッション詳細
   */
  getSession: async (sessionId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/session/${sessionId}`,
      { withCredentials: true }
    );
    return response.data;
  },
};

/**
 * 構造化面接テンプレートサービス
 */
export const templateService = {
  /**
   * テンプレート一覧を取得
   * @param {string|null} jobCategory - 職種カテゴリでフィルタ
   * @returns {Promise<Object>} テンプレート一覧
   */
  getTemplates: async (jobCategory = null) => {
    const params = jobCategory ? { job_category: jobCategory } : {};
    const response = await axios.get(
      `${BACKEND_URL}/interview_assistant/templates`,
      { withCredentials: true, params }
    );
    return response.data;
  },

  /**
   * テンプレート詳細を取得
   * @param {number} templateId - テンプレートID
   * @returns {Promise<Object>} テンプレート詳細
   */
  getTemplate: async (templateId) => {
    const response = await axios.get(
      `${BACKEND_URL}/interview_assistant/templates/${templateId}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * テンプレートを作成
   * @param {Object} templateData - テンプレートデータ
   * @returns {Promise<Object>} 作成されたテンプレート
   */
  createTemplate: async (templateData) => {
    const response = await axios.post(
      `${BACKEND_URL}/interview_assistant/templates`,
      templateData,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * テンプレートを更新
   * @param {number} templateId - テンプレートID
   * @param {Object} templateData - 更新データ
   * @returns {Promise<Object>} 更新されたテンプレート
   */
  updateTemplate: async (templateId, templateData) => {
    const response = await axios.put(
      `${BACKEND_URL}/interview_assistant/templates/${templateId}`,
      templateData,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * テンプレートを削除
   * @param {number} templateId - テンプレートID
   * @returns {Promise<void>}
   */
  deleteTemplate: async (templateId) => {
    await axios.delete(
      `${BACKEND_URL}/interview_assistant/templates/${templateId}`,
      { withCredentials: true }
    );
  },

  /**
   * テンプレートを複製
   * @param {number} templateId - テンプレートID
   * @param {string} newName - 新しいテンプレート名
   * @returns {Promise<Object>} 複製されたテンプレート
   */
  duplicateTemplate: async (templateId, newName) => {
    const response = await axios.post(
      `${BACKEND_URL}/interview_assistant/templates/${templateId}/duplicate`,
      { new_name: newName },
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * BARS評価を保存
   * @param {number} checkSheetId - チェックシートID
   * @param {Object} evaluationData - 評価データ
   * @returns {Promise<Object>} 保存された評価
   */
  saveBARSEvaluation: async (checkSheetId, evaluationData) => {
    const response = await axios.post(
      `${BACKEND_URL}/interview_assistant/check-sheets/${checkSheetId}/bars-evaluation`,
      evaluationData,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * BARS評価を取得
   * @param {number} checkSheetId - チェックシートID
   * @returns {Promise<Object>} 評価データ
   */
  getBARSEvaluation: async (checkSheetId) => {
    const response = await axios.get(
      `${BACKEND_URL}/interview_assistant/check-sheets/${checkSheetId}/bars-evaluation`,
      { withCredentials: true }
    );
    return response.data;
  },
};

/**
 * バイアス検出サービス
 */
export const biasService = {
  /**
   * バイアス分析を実行
   * @param {number} checkSheetId - チェックシートID
   * @param {Object} analysisData - 分析データ
   * @returns {Promise<Object>} バイアス分析結果
   */
  analyzeBias: async (checkSheetId, analysisData) => {
    const response = await axios.post(
      `${BACKEND_URL}/interview_assistant/check-sheets/${checkSheetId}/bias-analysis`,
      analysisData,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * バイアス分析結果を取得
   * @param {number} checkSheetId - チェックシートID
   * @returns {Promise<Object>} バイアス分析結果
   */
  getBiasAnalysis: async (checkSheetId) => {
    const response = await axios.get(
      `${BACKEND_URL}/interview_assistant/check-sheets/${checkSheetId}/bias-analysis`,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * 複数候補者のバイアス比較分析
   * @param {Array<number>} checkSheetIds - チェックシートID配列
   * @returns {Promise<Object>} 比較分析結果
   */
  compareBias: async (checkSheetIds) => {
    const response = await axios.get(
      `${BACKEND_URL}/interview_assistant/bias-analysis/comparison`,
      { withCredentials: true, params: { check_sheet_ids: checkSheetIds.join(',') } }
    );
    return response.data;
  },
};

/**
 * 自信度分析サービス
 */
export const confidenceService = {
  /**
   * 自信度分析結果を取得
   * @param {number} jobId - ジョブID
   * @returns {Promise<Object>} 自信度分析結果
   */
  getConfidenceAnalysis: async (jobId) => {
    const response = await axios.get(
      `${BACKEND_URL}/audio/results/confidence/${jobId}`,
      { withCredentials: true }
    );
    return response.data;
  },
};

export default {
  audio: audioService,
  analysis: analysisService,
  results: resultsService,
  audioOnly: audioOnlyService,
  templates: templateService,
  bias: biasService,
  confidence: confidenceService,
};
